import { useMemo } from "react";
import type { Bucket, Channel } from "../../types/analytics";
import { CHANNEL_COLOR, CHANNEL_LABEL, CHANNEL_ORDER, barGap, int, niceMax, pctStr, roasStr, sumChannels, yen } from "../../lib/analyticsCalc";

type ChartMetric = "sales" | "sessions" | "orders" | "ad";

const METRIC_TABS: { key: ChartMetric; label: string }[] = [
  { key: "sales", label: "売上" },
  { key: "sessions", label: "アクセス" },
  { key: "orders", label: "注文数" },
  { key: "ad", label: "広告費" },
];

interface TrendChartProps {
  buckets: Bucket[];
  channels: Record<Channel, boolean>;
  metric: ChartMetric;
  onMetricChange: (m: ChartMetric) => void;
  showCvrLine: boolean;
  onToggleCvrLine: () => void;
  hoverIndex: number | null;
  onHover: (i: number | null) => void;
}

function bucketValue(b: Bucket, active: Channel[], metric: ChartMetric): number {
  const m = sumChannels(b.by_channel, active);
  if (metric === "sales") return m.sales;
  if (metric === "sessions") return m.sessions ?? 0;
  if (metric === "orders") return m.orders;
  return m.ad_spend;
}

function tickFmt(v: number, metric: ChartMetric): string {
  if (metric === "sales" || metric === "ad") {
    return v >= 10000 ? (v / 10000).toLocaleString("ja-JP", { maximumFractionDigits: 1 }) + "万" : v.toLocaleString("ja-JP");
  }
  return v.toLocaleString("ja-JP");
}

export function TrendChart({ buckets, channels, metric, onMetricChange, showCvrLine, onToggleCvrLine, hoverIndex, onHover }: TrendChartProps) {
  const active = useMemo(() => CHANNEL_ORDER.filter((c) => channels[c]), [channels]);
  const n = buckets.length;

  const values = useMemo(() => buckets.map((b) => bucketValue(b, active, metric)), [buckets, active, metric]);
  const yMax = useMemo(() => niceMax(Math.max(...values, 1)), [values]);
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => ({ bottom: `${f * 100}%`, label: tickFmt(yMax * f, metric) }));

  const cvrs = useMemo(
    () =>
      buckets.map((b) => {
        const m = sumChannels(b.by_channel, active);
        return m.sessions ? m.orders / m.sessions : 0;
      }),
    [buckets, active]
  );
  const cMax = Math.max(0.005, Math.ceil((Math.max(...cvrs, 0) * 100) / 0.5) * 0.5 / 100);
  const cvrTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => ({ bottom: `${f * 100}%`, label: (cMax * f * 100).toFixed(1) + "%" }));
  const cvrPoints = buckets.map((_, i) => `${(((i + 0.5) / Math.max(1, n)) * 100).toFixed(2)},${(100 - (cvrs[i] / cMax) * 100).toFixed(2)}`).join(" ");

  const gap = barGap(n);
  const every = Math.max(1, Math.ceil(n / 10));

  const tip = hoverIndex != null ? buckets[hoverIndex] : null;
  const tipRight = hoverIndex != null && hoverIndex > n / 2;

  return (
    <div className="an-chart-card">
      <div className="an-chart-head">
        <div className="an-chart-title">推移</div>
        <div className="an-seg" role="group" aria-label="指標">
          {METRIC_TABS.map((m) => (
            <button key={m.key} className={`an-seg-btn small ${metric === m.key ? "active" : ""}`} onClick={() => onMetricChange(m.key)}>
              {m.label}
            </button>
          ))}
        </div>
        <label className="an-cvr-toggle">
          <input type="checkbox" checked={showCvrLine} onChange={onToggleCvrLine} />
          <span className="swatch" />
          転換率
        </label>
      </div>

      <div className="an-chart-body">
        <div className="an-chart-yaxis">
          {yTicks.map((t, i) => (
            <div className="an-chart-ytick" key={i} style={{ bottom: t.bottom, transform: "translateY(50%)" }}>
              {t.label}
            </div>
          ))}
        </div>

        <div className="an-chart-main">
          <div className="an-chart-plot" onMouseLeave={() => onHover(null)}>
            {yTicks.map((t, i) => (
              <div className="an-chart-gridline" key={i} style={{ bottom: t.bottom }} />
            ))}
            <div className="an-chart-bars" style={{ gap }}>
              {buckets.map((b, i) => {
                const tv = values[i] || 0;
                const h = yMax ? (tv / yMax) * 100 : 0;
                return (
                  <div
                    key={`${b.start}-${i}`}
                    className={`an-chart-bucket ${hoverIndex === i ? "hover" : ""}`}
                    onMouseEnter={() => onHover(i)}
                  >
                    <div className="an-chart-bar" style={{ height: `${h}%` }}>
                      {active
                        .slice()
                        .reverse()
                        .map((ch) => {
                          const chVal =
                            metric === "sales"
                              ? b.by_channel[ch].sales
                              : metric === "sessions"
                              ? b.by_channel[ch].sessions ?? 0
                              : metric === "orders"
                              ? b.by_channel[ch].orders
                              : b.by_channel[ch].ad_spend;
                          const segH = tv ? (chVal / tv) * 100 : 0;
                          return <div className="an-chart-seg" key={ch} style={{ height: `${segH}%`, background: CHANNEL_COLOR[ch] }} />;
                        })}
                    </div>
                  </div>
                );
              })}
            </div>
            {showCvrLine && (
              <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="an-chart-cvrline">
                <polyline points={cvrPoints} fill="none" stroke="#1C1B19" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
              </svg>
            )}
            {tip && (
              <div
                className="an-tooltip"
                style={{
                  left: `${((tipRight ? (hoverIndex as number) : (hoverIndex as number) + 1) / Math.max(1, n)) * 100}%`,
                  transform: tipRight ? "translateX(calc(-100% - 8px))" : "translateX(8px)",
                }}
              >
                <div className="an-tooltip-title">{tip.label}</div>
                {tip.events.length > 0 && <div className="an-tooltip-event">{tip.events.map((e) => e.name).join(" ・ ")}</div>}
                {active.map((ch) => (
                  <div className="an-tooltip-row" key={ch}>
                    <span className="dot" style={{ background: CHANNEL_COLOR[ch] }} />
                    <span className="name">{CHANNEL_LABEL[ch]}</span>
                    <span className="val">{metric === "sales" || metric === "ad" ? yen(bucketMetricValue(tip.by_channel[ch], metric)) : int(bucketMetricValue(tip.by_channel[ch], metric))}</span>
                  </div>
                ))}
                {(() => {
                  const t = sumChannels(tip.by_channel, active);
                  return (
                    <div className="an-tooltip-foot">
                      <div className="an-tooltip-foot-row">
                        <span className="label">合計</span>
                        <span className="val strong">{metric === "sales" || metric === "ad" ? yen(bucketValue(tip, active, metric)) : int(bucketValue(tip, active, metric))}</span>
                      </div>
                      <div className="an-tooltip-foot-row">
                        <span className="label">アクセス / 注文</span>
                        <span className="val">
                          {t.sessions != null ? int(t.sessions) : "—"} / {int(t.orders)}
                        </span>
                      </div>
                      <div className="an-tooltip-foot-row">
                        <span className="label">転換率</span>
                        <span className="val">{t.sessions ? pctStr(t.orders, t.sessions) : "—"}</span>
                      </div>
                      <div className="an-tooltip-foot-row">
                        <span className="label">広告費 / ROAS</span>
                        <span className="val">
                          {yen(t.ad_spend)} / {roasStr(t.sales, t.ad_spend)}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
          <div className="an-chart-xlabels" style={{ gap }}>
            {buckets.map((b, i) => (
              <div className="an-chart-xlabel-cell" key={`${b.start}-x-${i}`}>
                <span className="an-chart-xlabel">{i % every === 0 ? b.label : ""}</span>
              </div>
            ))}
          </div>
        </div>

        {showCvrLine && (
          <div className="an-chart-yaxis right">
            {cvrTicks.map((t, i) => (
              <div className="an-chart-ytick" key={i} style={{ bottom: t.bottom, transform: "translateY(50%)" }}>
                {t.label}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="an-chart-legend">
        {active.map((ch) => (
          <div className="an-legend-item" key={ch}>
            <span className="an-legend-dot" style={{ background: CHANNEL_COLOR[ch] }} />
            {CHANNEL_LABEL[ch]}
          </div>
        ))}
      </div>
    </div>
  );
}

function bucketMetricValue(m: { sales: number; orders: number; sessions: number | null; ad_spend: number }, metric: ChartMetric): number {
  if (metric === "sales") return m.sales;
  if (metric === "sessions") return m.sessions ?? 0;
  if (metric === "orders") return m.orders;
  return m.ad_spend;
}
